import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, getErrorMessage } from './client';
import type {
  Conductor,
  CreateConductorDto,
  CreateEmpresaDto,
  CreateEnvioDto,
  CreateOrigenDestinoDto,
  CreateServicioDto,
  CreateUsuarioDto,
  CreateVehiculoDto,
  CrearDeCAInput,
  DeCaEnriched,
  DeCaVersion,
  Empresa,
  Envio,
  EnvioEstado,
  ModificarDeCAInput,
  OrigenDestino,
  Servicio,
  UpdateConductorDto,
  UpdateEmpresaDto,
  UpdateEnvioDto,
  UpdateOrigenDestinoDto,
  UpdateUsuarioDto,
  UpdateVehiculoDto,
  Usuario,
  Vehiculo,
} from './types';

// ──────── Empresas ────────
export function useEmpresas() {
  const api = useApi();
  return useQuery({
    queryKey: ['empresas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
  });
}

export function useEmpresa(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['empresas', id],
    queryFn: () => api.get<Empresa>(`/empresas/${id}`),
    enabled: !!id,
  });
}

export function useCreateEmpresa() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Empresa, unknown, CreateEmpresaDto>({
    mutationFn: (data) => api.post<Empresa>('/empresas', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresas'] }),
  });
}

export function useUpdateEmpresa() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Empresa, unknown, { id: string; data: UpdateEmpresaDto }>({
    mutationFn: ({ id, data }) => api.patch<Empresa>(`/empresas/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['empresas'] });
      qc.invalidateQueries({ queryKey: ['empresas', vars.id] });
    },
  });
}

export function useDeleteEmpresa() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Empresa, unknown, string>({
    mutationFn: (id) => api.del<Empresa>(`/empresas/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['empresas'] }),
  });
}

// ──────── Vehículos ────────
export function useVehiculos() {
  const api = useApi();
  return useQuery({
    queryKey: ['vehiculos'],
    queryFn: () => api.get<Vehiculo[]>('/vehiculos'),
  });
}

export function useVehiculo(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['vehiculos', id],
    queryFn: () => api.get<Vehiculo>(`/vehiculos/${id}`),
    enabled: !!id,
  });
}

export function useCreateVehiculo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Vehiculo, unknown, CreateVehiculoDto>({
    mutationFn: (data) => api.post<Vehiculo>('/vehiculos', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehiculos'] }),
  });
}

export function useUpdateVehiculo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Vehiculo, unknown, { id: string; data: UpdateVehiculoDto }>({
    mutationFn: ({ id, data }) => api.patch<Vehiculo>(`/vehiculos/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['vehiculos'] });
      qc.invalidateQueries({ queryKey: ['vehiculos', vars.id] });
    },
  });
}

export function useDeleteVehiculo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Vehiculo, unknown, string>({
    mutationFn: (id) => api.del<Vehiculo>(`/vehiculos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehiculos'] }),
  });
}

// ──────── Conductores ────────
export function useConductores() {
  const api = useApi();
  return useQuery({
    queryKey: ['conductores'],
    queryFn: () => api.get<Conductor[]>('/conductores'),
  });
}

export function useConductor(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['conductores', id],
    queryFn: () => api.get<Conductor>(`/conductores/${id}`),
    enabled: !!id,
  });
}

export function useCreateConductor() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Conductor, unknown, CreateConductorDto>({
    mutationFn: (data) => api.post<Conductor>('/conductores', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conductores'] }),
  });
}

export function useUpdateConductor() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Conductor, unknown, { id: string; data: UpdateConductorDto }>({
    mutationFn: ({ id, data }) => api.patch<Conductor>(`/conductores/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['conductores'] });
      qc.invalidateQueries({ queryKey: ['conductores', vars.id] });
    },
  });
}

export function useDeleteConductor() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Conductor, unknown, string>({
    mutationFn: (id) => api.del<Conductor>(`/conductores/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conductores'] }),
  });
}

// ──────── Usuarios (backoffice) ────────
export function useUsuarios() {
  const api = useApi();
  return useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get<Usuario[]>('/usuarios'),
  });
}

export function useUsuario(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['usuarios', id],
    queryFn: () => api.get<Usuario>(`/usuarios/${id}`),
    enabled: !!id,
  });
}

export function useCreateUsuario() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Usuario, unknown, CreateUsuarioDto>({
    mutationFn: (data) => api.post<Usuario>('/usuarios', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });
}

export function useUpdateUsuario() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Usuario, unknown, { id: string; data: UpdateUsuarioDto }>({
    mutationFn: ({ id, data }) => api.patch<Usuario>(`/usuarios/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      qc.invalidateQueries({ queryKey: ['usuarios', vars.id] });
    },
  });
}

export function useDeleteUsuario() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Usuario, unknown, string>({
    mutationFn: (id) => api.del<Usuario>(`/usuarios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  });
}

// ──────── Orígenes / Destinos ────────
export function useOrigenesDestinos() {
  const api = useApi();
  return useQuery({
    queryKey: ['origenes-destinos'],
    queryFn: () => api.get<OrigenDestino[]>('/origenes-destinos'),
  });
}

export function useOrigenDestino(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['origenes-destinos', id],
    queryFn: () => api.get<OrigenDestino>(`/origenes-destinos/${id}`),
    enabled: !!id,
  });
}

export function useCreateOrigenDestino() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<OrigenDestino, unknown, CreateOrigenDestinoDto>({
    mutationFn: (data) => api.post<OrigenDestino>('/origenes-destinos', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['origenes-destinos'] }),
  });
}

export function useUpdateOrigenDestino() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<OrigenDestino, unknown, { id: string; data: UpdateOrigenDestinoDto }>({
    mutationFn: ({ id, data }) => api.patch<OrigenDestino>(`/origenes-destinos/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['origenes-destinos'] });
      qc.invalidateQueries({ queryKey: ['origenes-destinos', vars.id] });
    },
  });
}

export function useDeleteOrigenDestino() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<OrigenDestino, unknown, string>({
    mutationFn: (id) => api.del<OrigenDestino>(`/origenes-destinos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['origenes-destinos'] }),
  });
}

// ──────── Envíos ────────
export function useEnvios() {
  const api = useApi();
  return useQuery({
    queryKey: ['envios'],
    queryFn: () => api.get<Envio[]>('/envios'),
  });
}

export function useEnvio(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['envios', id],
    queryFn: () => api.get<Envio>(`/envios/${id}`),
    enabled: !!id,
  });
}

export function useCreateEnvio() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Envio, unknown, CreateEnvioDto>({
    mutationFn: (data) => api.post<Envio>('/envios', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['envios'] }),
  });
}

export function useEmitirDeCA() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DeCaEnriched, unknown, { envioId: string; servicios: CreateServicioDto[] }>({
    mutationFn: ({ envioId, servicios }) =>
      api.post<DeCaEnriched>(`/envios/${envioId}/deca`, { servicios }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envios'] });
      qc.invalidateQueries({ queryKey: ['deca'] });
    },
  });
}

/**
 * Crear DeCA en una sola llamada atómica (Sprint 4): crea el envío en
 * estado EMITIDO y emite el DeCA contra sus servicios. Pensado para el
 * backoffice: el operario rellena el wizard completo y emite al final.
 *
 * Invalida envios + deca (el nuevo DeCA aparece en `/deca` y el envío
 * resultante en `/envios`).
 */
export function useCrearDeCA() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DeCaEnriched, unknown, CrearDeCAInput>({
    mutationFn: (input) => api.post<DeCaEnriched>('/deca', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['envios'] });
      qc.invalidateQueries({ queryKey: ['deca'] });
    },
  });
}

export function useUpdateEnvio() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Envio, unknown, { id: string; data: UpdateEnvioDto }>({
    mutationFn: ({ id, data }) => api.patch<Envio>(`/envios/${id}`, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['envios'] });
      qc.invalidateQueries({ queryKey: ['envios', vars.id] });
    },
  });
}

/**
 * Cambio de estado del envio (Sprint 5.3). El backend expone
 * `PATCH /envios/:id/estado` con `{ estado: EstadoEnvio }` — un canal
 * dedicado (no PATCH /envio/:id generico) para que el servidor valide
 * transiciones legales (BORRADOR -> EMITIDO -> EN_CURSO -> ...).
 *
 * Invalidamos también la cache de DeCA porque el `estado` del DeCA
 * se deriva del estado del envío (ver `mapEnvioEstadoToDeCaEstado`).
 * Si el operario cambia el estado desde la vista del DeCA, el detalle
 * y la lista de DeCAs tienen que refrescarse automáticamente.
 */
export function useUpdateEnvioEstado() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<Envio, unknown, { id: string; estado: EnvioEstado }>({
    mutationFn: ({ id, estado }) => api.patch<Envio>(`/envios/${id}/estado`, { estado }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['envios'] });
      qc.invalidateQueries({ queryKey: ['envios', vars.id] });
      qc.invalidateQueries({ queryKey: ['deca'] });
    },
  });
}

// ──────── DeCA ────────
export function useDeCAs() {
  const api = useApi();
  return useQuery({
    queryKey: ['deca'],
    queryFn: () => api.get<DeCaEnriched[]>('/deca'),
  });
}

export function useDeCA(id?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['deca', id],
    queryFn: () => api.get<DeCaEnriched>(`/deca/${id}`),
    enabled: !!id,
  });
}

export function useVersionesDeCA(decaId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['deca', decaId, 'versiones'],
    queryFn: () => api.get<DeCaVersion[]>(`/deca/${decaId}/versiones`),
    enabled: !!decaId,
  });
}

/**
 * Sprint 5.3: drill-down de una version concreta.
 * Devuelve la DeCaVersion con su envioSnapshot completo.
 */
export function useVersionDeCA(decaId?: string, n?: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['deca', decaId, 'versiones', n],
    queryFn: () => api.get<DeCaVersion>(`/deca/${decaId}/versiones/${n}`),
    enabled: !!decaId && typeof n === 'number',
  });
}

export function useModificarDeCA() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DeCaEnriched, unknown, { decaId: string; input: ModificarDeCAInput }>({
    mutationFn: ({ decaId, input }) => api.post<DeCaEnriched>(`/deca/${decaId}/modificar`, input),
    onSuccess: (_data, vars) => {
      // Invalidamos el DeCA concreto y su lista de versiones para que la
      // vista se refresque automáticamente.
      qc.invalidateQueries({ queryKey: ['deca', vars.decaId] });
      qc.invalidateQueries({ queryKey: ['deca', vars.decaId, 'versiones'] });
      qc.invalidateQueries({ queryKey: ['deca'] }); // listado general
    },
  });
}

export { getErrorMessage };

// ──────── Servicios (sub-recurso de un DeCA) ────────
export function useServicios(decaId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['deca', decaId, 'servicios'],
    queryFn: () => api.get<Servicio[]>(`/deca/${decaId}/servicios`),
    enabled: !!decaId,
  });
}
